<template>
    <div class="opportunity-types-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Opportunity Types</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="add-opportunity-types-buttons-wrapper">
                    <app-button v-if="!isAddOpportunityTypeShown" @click="showAddOpportunityTypeSection">
                        Add New
                    </app-button>
                </div>
                <div class="add-opportunity-type-wrapper" v-if="isAddOpportunityTypeShown">
                    <div class="flex-wrapper">
                        <h3 class="upper-text">Add New Opportunity Type</h3>
                        <app-input
                            v-model="opportunityType.name" 
                            :label="'Name'" />
                    </div>
                    <div class="actions">
                        <app-button 
                            @click="saveNewOpportunityType" 
                            :loading="isSavingNewOpportunityType" 
                            :disabled="isSavingNewOpportunityType"
                            size="small">
                            Save
                        </app-button>
                        <app-button 
                            class="red-btn"
                            @click="hideAddOpportunityTypeSection" 
                            size="small">
                            Cancel
                        </app-button>
                    </div>
                </div>
                <div class="opportunity-types-wrapper">
                    <div v-for="opportunityType in opportunityTypes" :class="['opportunity-type', { 'editing': opportunityType.isEditShown }]" :key="opportunityType.id">
                        <div class="opportunity-type-wrapper">
                            <div class="opportunity-type-info">
                                <p v-if="!opportunityType.isEditShown">{{ opportunityType.name.value }}</p>
                                <app-input
                                    v-else
                                    v-model="opportunityType.name.edit" 
                                    :label="'Name'" />
                            </div>
                        </div>
                        <div v-if="opportunityType.isEditShown" class="opportunity-type-edit-buttons-wrapper">
                            <app-button
                                @click="updateOpportunityType(opportunityType)"
                                size="small"
                                :disabled="opportunityType.isUpdating"
                                :loading="opportunityType.isUpdating">
                                Save
                            </app-button>
                            <app-button 
                                @click="cancelOpportunityTypeEdit(opportunityType)"
                                size="small"
                                class="red-btn"
                                :disabled="opportunityType.isUpdating"
                                :loading="opportunityType.isUpdating">
                                Cancel
                            </app-button>
                        </div>
                        <div v-else class="opportunity-type-edit-buttons-wrapper">
                            <app-button
                                @click="showOpportunityTypeEdit(opportunityType)"
                                size="small"
                                :disabled="opportunityType.isDeleting">
                                Edit
                            </app-button>
                            <app-button
                                @click="deleteOpportunityType(opportunityType)"
                                class="red-btn"
                                size="small"
                                :disabled="opportunityType.isDeleting"
                                :loading="opportunityType.isDeleting">
                                Delete
                            </app-button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" :onClick="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'opportunity-types-widget',

    props: {
        options: {
            type: Object
        },

        widget: {
            type: Object,
            required: true
        }
    },

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'widget-close-svg': WidgetCloseSvg
    },

    data() {
        return {
            opportunityTypes: [],
            isAddOpportunityTypeShown: false,
            isSavingNewOpportunityType: false,
            opportunityType: {
                name: ''
            },
            clientId: null
        }
    },

    methods: {
        getOpportunityTypes() {
            this.$api
                .get(`/service/DealTypes/?$filter=ClientId eq ${ this.clientId }`)
                .then(response => {
                    var opportunityTypeResponse = response.data.value;
                    this.setOpportunityTypes(opportunityTypeResponse);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while getting your opportunity types', { error });
                });
        },

        setOpportunityTypes(response) {
            this.opportunityTypes = response.map(opportunityType => {
                return {
                    id: opportunityType.Id,
                    name: {
                        value: opportunityType.Name,
                        edit: opportunityType.Name
                    },
                    isEditShown: false,
                    isUpdating: false,
                    isDeleting: false
                };
            });
        },

        showAddOpportunityTypeSection() {
            this.isAddOpportunityTypeShown = true;
        },

        hideAddOpportunityTypeSection() {
            this.isAddOpportunityTypeShown = false;
        },

        saveNewOpportunityType() {
            this.isSavingNewOpportunityType = true;

            var request = {
                Name: this.opportunityType.name,
                ClientId: this.clientId
            }

            this.$api
                .post('/api/DealTypes/', request)
                .then(response => {
                    this.isSavingNewOpportunityType = false;
                    this.isAddOpportunityTypeShown = false;

                    this.opportunityTypes.unshift({
                        id: response.data.Id,
                        name: {
                            value: response.data.Name,
                            edit: response.data.Name
                        },
                        isEditShown: false,
                        isUpdating: false,
                        isDeleting: false
                    });



                    this.clearOpportunityType();
                    EventBus.$emit('opportunity-type:update');
                })
                .catch((error) => {
                    this.isSavingNewOpportunityType = false;
                    this.$helpers.showFailNotification('An error occurred while creating your opportunity type.', { error })
                });
        },

        clearOpportunityType() {
            this.opportunityType.name = '';
        },

        updateOpportunityType(opportunityType) {
            opportunityType.isUpdating = true;

            var changes = [{
                'name': 'Name', 
                'value': opportunityType.name.edit
            }];

            this.$api
                .patch(`/api/DealTypes/${ opportunityType.id }`, changes)
                .then(() => {
                    opportunityType.isUpdating = false;
                    opportunityType.isEditShown = false;

                    opportunityType.name.value = opportunityType.name.edit;
                    EventBus.$emit('opportunity-type:update');
                })
                
                .catch((error) => {
                    this.isUpdating = false;
                    opportunityType.isEditShown = false;
                    this.$helpers.showFailNotification('An error occurred while updating your opportunity type.', { error })
                });
        },

        showOpportunityTypeEdit(opportunityType) {
            opportunityType.isEditShown = true;
        },

        cancelOpportunityTypeEdit(opportunityType) {
            opportunityType.name.edit = opportunityType.name.value;
            opportunityType.isEditShown = false;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        deleteOpportunityType(opportunityType) {
            opportunityType.isDeleting = true;

            this.$api
                .delete(`/api/DealTypes/${ opportunityType.id }`)
                
                .then(() => {
                    var matchedOpportunityType = this.opportunityTypes.find(type => {
                        return type.id === opportunityType.id;
                    });

                    this.opportunityTypes.splice(this.opportunityTypes.indexOf(matchedOpportunityType), 1);
                    this.$helpers.showSuccessNotification(`The "${ opportunityType.name.edit }" opportunity type has been removed.`);
                    EventBus.$emit('opportunity-type:update');
                })
                .catch((error) => {
                    opportunityType.isEditShown = false;
                    this.$helpers.showFailNotification(`An error occurred while deleting "${ opportunityType.name.edit }".`, { error });
                })
                .finally(() => {
                    opportunityType.isDeleting = false;
                });
        },
        
    },

    created() {
        this.clientId = this.widget.options.clientId;
        this.getOpportunityTypes();
    }
}
</script>

<style>
.opportunity-types-widget .flex-wrapper {
    display: flex;
    align-items: flex-end;
    margin-right: 15px;
}

.opportunity-types-widget .content-wrapper .add-opportunity-types-buttons-wrapper .btn {
    margin-bottom: 25px;
}

.opportunity-types-widget .content-wrapper .add-opportunity-type-wrapper {
    margin-bottom: 25px;
    padding: 25px 15px 15px 15px;
    border: 2px solid #E9E9EF;
    border-radius: 20px;
    position: relative;
}

.opportunity-types-widget .content-wrapper .add-opportunity-type-wrapper .app-input {
    width: 100%;
    margin-bottom: 10px;
}

.opportunity-types-widget .content-wrapper .add-opportunity-type-wrapper .upper-text {
    position: absolute;
    top: -10px;
    left: 15px;
    background: #FFFFFF;
    padding: 0px 15px;
}

.opportunity-types-widget .content-wrapper .add-opportunity-type-wrapper .actions {
    display: flex;
    justify-content: center;
}

.opportunity-types-widget .content-wrapper .add-opportunity-type-wrapper .actions .btn {
    margin: 0 5px;
}

.opportunity-types-widget .content-wrapper .opportunity-types-wrapper .opportunity-type {
    padding: 5px 5px 5px 10px;
    border-radius: 20px;
    border: 2px solid #E9E9EF;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
}

.opportunity-types-widget .content-wrapper .opportunity-types-wrapper .opportunity-type .opportunity-type-wrapper,
.opportunity-types-widget .content-wrapper .opportunity-types-wrapper .opportunity-type .opportunity-type-wrapper .opportunity-type-info {
    display: flex;
    align-items: center;
}

.opportunity-types-widget .content-wrapper .opportunity-types-wrapper .opportunity-type .opportunity-type-wrapper p {
    color: #877D88;
    font-size: 16px;
    margin-bottom: 0;
}

.opportunity-types-widget .content-wrapper .opportunity-types-wrapper .opportunity-type .opportunity-type-edit-buttons-wrapper {
    display: flex;
    align-items: center;
}

.opportunity-types-widget .content-wrapper .opportunity-types-wrapper .opportunity-type .opportunity-type-edit-buttons-wrapper .btn:nth-child(1) {
    margin-right: 10px;
}

.opportunity-types-widget .red-btn {
    background: #F30070;
    border: 1px solid #F30070;
    color: #FFFFFF;
}

.opportunity-types-widget .red-btn:hover {
    background: #D10E68;
    border: 1px solid #D10E68;
}
</style>